/**
 * De tekens die het koppelvlak draagt: de productie `Char` van XML 1.0 (§2.2, koppelvlak hoofdstuk 3).
 *
 * Dat zijn tab, regeleinde en wagenterugloop, en verder U+0020 tot en met U+D7FF, U+E000 tot en met
 * U+FFFD en U+10000 tot en met U+10FFFF. Daarbuiten vallen de stuurtekens U+0000 tot en met U+001F op
 * die drie na, U+FFFE en U+FFFF, en een losse helft van een surrogaatpaar. Een document met zo'n teken
 * is niet well-formed, ook niet als het teken als tekenverwijzing (`&#1;`) of in een CDATA-blok staat,
 * en een strikte lezer weigert dan het hele bericht. Zulke tekens komen bijna altijd mee met geplakte
 * tekst, uit een pdf of een tekstverwerker, en zijn op het scherm onzichtbaar.
 *
 * Eén definitie voor beide kanten. De engine weigert een antwoord dat zo'n teken bevat, zodat de
 * invuller het hoort voordat hij indient (ontwerp 5.9); de bouwers van het XML laten het weg, zodat
 * ook wat al is opgeslagen nooit een bericht breekt.
 *
 * Met de vlag `u` leest de reguliere expressie codepunten en geen UTF-16-eenheden: een geldig paar is
 * één codepunt boven U+FFFF en valt in het toegestane bereik, een losse helft is een codepunt in
 * U+D800 tot en met U+DFFF en valt erbuiten.
 */

const FORBIDDEN = /[^\t\n\r -퟿-�\u{10000}-\u{10FFFF}]/u
const FORBIDDEN_ALL = new RegExp(FORBIDDEN.source, 'gu')

/** Of deze tekst een teken bevat dat XML 1.0 niet toelaat. */
export function hasForbiddenXmlCharacter(text: string): boolean {
  return FORBIDDEN.test(text)
}

/** Deze tekst zonder de tekens die XML 1.0 niet toelaat; de rest blijft precies staan. */
export function withoutForbiddenXmlCharacters(text: string): string {
  return text.replace(FORBIDDEN_ALL, '')
}

/**
 * Of ergens in deze waarde een tekst staat met een teken dat XML 1.0 niet toelaat: in een tekst, in
 * een lijst of in een object, op elke diepte. Voor een antwoord met zijn structuur, rijen of bijlagen.
 */
export function containsForbiddenXmlCharacter(value: unknown): boolean {
  if (typeof value === 'string') return hasForbiddenXmlCharacter(value)
  if (Array.isArray(value)) return value.some(containsForbiddenXmlCharacter)
  if (value !== null && typeof value === 'object') return Object.values(value).some(containsForbiddenXmlCharacter)
  return false
}
